import { tr } from '@/lib/i18n-core';
import { ChevronDown } from 'lucide-react';
import { Badge, Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@/components/ui/primitives';

export interface DepartmentChoice {
  id: string;
  name: string;
}

export function toggleDepartment(value: readonly string[], id: string, on: boolean): string[] {
  if (!on) return value.filter((item) => item !== id);
  return value.includes(id) ? [...value] : [...value, id];
}

export function departmentSummary(options: readonly DepartmentChoice[], value: readonly string[]): string {
  if (value.length === 0) return '';
  const names = value.map((id) => options.find((option) => option.id === id)?.name).filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(', ') : tr('admin', 'person_x_departments', { count: value.length });
}

export function DepartmentsField({
  options,
  value,
  onChange,
  disabled,
}: {
  options: readonly DepartmentChoice[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const chosen = new Set(value);
  const summary = departmentSummary(options, value);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="w-full justify-between font-normal">
          <span className="truncate text-start">{summary || tr('admin', 'choose_departments')}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-1">
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {options.map((option) => (
            <label key={option.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
              <Checkbox checked={chosen.has(option.id)} onCheckedChange={(on) => onChange(toggleDepartment(value, option.id, on))} />
              <span className="min-w-0 flex-1 truncate">{option.name}</span>
              {value[0] === option.id && <Badge variant="muted">{tr('admin', 'main_department')}</Badge>}
            </label>
          ))}
          {options.length === 0 && <p className="px-1 py-1 text-sm text-muted-foreground">{tr('admin', 'this_branch_has_no_departments_yet')}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
